// src/components/magazines/CoverThumb.jsx
import React, { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";

// Use the ESM worker (pdfjs >= 3)
const pdfjsWorker = new Worker(
  new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url),
  { type: "module" }
);
pdfjs.GlobalWorkerOptions.workerPort = pdfjsWorker;

// Cache by src + target width for reuse across the grid
const cache = new Map(); // key: `${src}|${width}` -> dataURL

// Simple concurrency limiter to avoid overloading PDF.js when many covers are visible.
// Allow up to N thumbnails to render concurrently; others wait.
const MAX_PARALLEL = 8; // Optimal for modern browsers with shared PDF.js worker
let inflight = 0;
const waitQueue = [];
function acquireSlot() {
  return new Promise((resolve) => {
    if (inflight < MAX_PARALLEL) {
      inflight++;
      resolve(() => releaseSlot());
    } else {
      waitQueue.push(resolve);
    }
  });
}
function releaseSlot() {
  inflight = Math.max(0, inflight - 1);
  const next = waitQueue.shift();
  if (next) {
    inflight++;
    next(() => releaseSlot());
  }
}

/**
 * Renders page 1 of the PDF as a thumbnail <img>.
 * - Prefers pre-generated thumbnail from coverKey if available
 * - Falls back to client-side PDF rendering
 * - Lazy-loads with IntersectionObserver
 * - HiDPI-aware (crisp on retina)
 * - Caches rendered data URLs per (src,width)
 */
export default function CoverThumb({
  src,
  coverKey = null,        // Path to pre-generated thumbnail (e.g., "covers/slug-cover.jpg")
  publicUrl = "",         // Base URL for R2 (e.g., "https://media.22westmedia.com")
  width = 360,            // CSS pixel width target for the thumb
  quality = 0.7,          // JPEG quality - 0.7 is optimal balance for thumbnails
  title = "Magazine cover",// used for alt text
  className = "",
  showSpinner = true,      // show a spinner while rendering page 1
}) {
  // If we have a pre-generated thumbnail, use it directly (FAST PATH)
  const thumbnailUrl = coverKey && publicUrl 
    ? `${publicUrl.replace(/\/$/, "")}/${coverKey}` 
    : null;

  const key = `${src}|${width}`;
  const [dataUrl, setDataUrl] = useState(cache.get(key) || null);
  const [, setLoading] = useState(!cache.get(key));
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const ioRef = useRef(null);
  const [isVisible, setIsVisible] = useState(!!cache.get(key));

  // If we have a thumbnail URL, we don't need the PDF rendering logic
  const useThumbnail = Boolean(thumbnailUrl);

  // Lazy-load when near the viewport
  useEffect(() => {
    if (useThumbnail) return; // Skip lazy loading setup if using thumbnail
    if (isVisible || cache.get(key)) return;
    ioRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          ioRef.current?.disconnect();
        }
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 }
    );
    if (ref.current) ioRef.current.observe(ref.current);
    return () => ioRef.current?.disconnect();
  }, [isVisible, key, useThumbnail]);

  // Render first page to a canvas -> dataURL (FALLBACK for old magazines without pre-generated thumbnails)
  useEffect(() => {
    // If using pre-generated thumbnail, skip PDF rendering entirely
    if (useThumbnail) return;
    
    let cancelled = false;
    let loadingTask = null;
    let release = null; // Track release function for cleanup
    let released = false; // Track if already released to prevent double-release

    async function run() {
      if (!isVisible || cache.get(key)) return;
      
      setLoading(true);
      setError(null);
      try {
        release = await acquireSlot();
        try {
          // Keep it simple - use same config as FlipbookViewer that loads fast
          loadingTask = pdfjs.getDocument({
            url: src,
            stopAtErrors: false,
          });
          const doc = await loadingTask.promise;
          const page = await doc.getPage(1);

          // Calculate scale like FlipbookViewer (more efficient)
          const v1 = page.getViewport({ scale: 1 });
          const dpr = Math.min(window.devicePixelRatio || 1, 2); // Cap at 2x for thumbnails
          const scale = (width / v1.width) * dpr;
          const viewport = page.getViewport({ scale });

          // Render at actual pixel size (no transform needed)
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          
          const ctx = canvas.getContext("2d", { alpha: false });
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
          }

          try {
            await page.render({ 
              canvasContext: ctx, 
              viewport,
            }).promise;
            
            const url = canvas.toDataURL("image/jpeg", quality);
            cache.set(key, url);
            if (!cancelled) setDataUrl(url);
          } catch (renderError) {
            // Fallback to 1x if out of memory
            if (!cancelled && renderError?.message?.includes('memory')) {
              const scale1x = width / v1.width;
              const viewport1x = page.getViewport({ scale: scale1x });
              canvas.width = Math.floor(viewport1x.width);
              canvas.height = Math.floor(viewport1x.height);
              
              await page.render({ 
                canvasContext: ctx, 
                viewport: viewport1x,
              }).promise;
              
              const url = canvas.toDataURL("image/jpeg", quality);
              cache.set(key, url);
              if (!cancelled) setDataUrl(url);
            } else {
              throw renderError;
            }
          }
        } finally {
          // Release slot ONCE when done (success or error)
          if (release && !released) {
            released = true;
            release();
            release = null;
          }
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
      
      // Clean up async - release slot if not already released, then destroy task
      (async () => {
        if (release && !released) {
          released = true;
          release();
        }
        if (loadingTask) {
          try {
            await loadingTask.destroy();
          } catch (err) {
            // Silently ignore all cleanup errors
          }
        }
      })();
    };
  }, [isVisible, key, src, width, quality, useThumbnail]);

  return (
  <div ref={ref} className={`relative aspect-[17/22] w-full overflow-hidden ${className}`}>
      {/* Fast path: Use pre-generated thumbnail if available */}
      {useThumbnail && thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            console.warn(`Thumbnail failed to load: ${thumbnailUrl}, falling back to PDF rendering`);
            // On thumbnail load error, fallback to PDF rendering by clearing useThumbnail
            // This is a simple approach; in production you might want more sophisticated fallback
            e.target.style.display = 'none';
          }}
        />
      ) : dataUrl ? (
        <img
          src={dataUrl}
          alt={title ? `${title} — cover` : "Magazine cover"}
      className="absolute inset-0 w-full h-full object-cover block"
          loading="lazy"
          decoding="async"
        />
      ) : error ? (
        <div
          className="absolute inset-0 grid place-items-center bg-neutral-200 text-neutral-500 text-sm"
          role="img"
          aria-label="Failed to load cover"
        >
          Failed to load cover
        </div>
      ) : (
        <div className="absolute inset-0 bg-neutral-200" aria-hidden="true">
          {showSpinner && (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="h-10 w-10 text-[#191f5e]"
                viewBox="0 0 24 24"
                fill="none"
                role="status"
                aria-label="Loading cover"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="gray"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="60"
                  strokeDashoffset="20"
                  opacity="0.9"
                  fill="none"
                >
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 12 12"
                    to="360 12 12"
                    dur="0.9s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
